import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SKILLS } from '../data';

const CATS = Object.keys(SKILLS);
const CAT_ICON = { Frontend:'⚛️', Backend:'🔧', Database:'🗄️', Tools:'🛠️', 'AI & ERP':'🤖' };

function Bar({ name, pct, icon, i }) {
  return (
    <motion.div initial={{opacity:0,x:-16}} whileInView={{opacity:1,x:0}}
      transition={{delay:i*0.07,duration:0.45}} viewport={{once:true}}
      className="group mb-5 last:mb-0"
    >
      <div className="flex justify-between items-center mb-1.5">
        <span className="flex items-center gap-2 text-slate-300 text-sm font-medium group-hover:text-white transition-colors">
          <span>{icon}</span>{name}
        </span>
        <span className="text-violet-400 text-xs font-mono font-bold">{pct}%</span>
      </div>
      <div className="h-1.5 bg-white/[0.05] rounded-full overflow-hidden">
        <motion.div
          className="h-full progress rounded-full"
          initial={{ width:0 }}
          whileInView={{ width:`${pct}%` }}
          transition={{ duration:1.1, delay:i*0.07+0.25, ease:'easeOut' }}
          viewport={{ once:true }}
        />
      </div>
    </motion.div>
  );
}

export default function Skills() {
  const [active, setActive] = useState('Frontend');

  return (
    <section id="skills" className="section-pad relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-violet-950/8 to-transparent pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <motion.div initial={{opacity:0,y:28}} whileInView={{opacity:1,y:0}}
          transition={{duration:0.55}} viewport={{once:true}} className="text-center mb-16">
          <p className="text-violet-400 font-mono text-xs tracking-[0.2em] uppercase mb-4">— Technical Skills</p>
          <h2 className="text-4xl sm:text-5xl font-black text-white mb-4">
            My Tech <span className="grad-text">Toolkit</span>
          </h2>
          <p className="text-slate-400 max-w-md mx-auto">7+ years of hands-on experience across the full stack</p>
        </motion.div>

        {/* Category tabs */}
        <motion.div initial={{opacity:0,y:16}} whileInView={{opacity:1,y:0}}
          transition={{duration:0.45,delay:0.1}} viewport={{once:true}}
          className="flex flex-wrap justify-center gap-2 mb-12"
        >
          {CATS.map(cat => (
            <motion.button key={cat} onClick={() => setActive(cat)}
              whileHover={{scale:1.05}} whileTap={{scale:0.95}}
              className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 flex items-center gap-2 ${
                active === cat
                  ? 'bg-violet-600 text-white shadow-lg shadow-violet-600/25'
                  : 'glass text-slate-400 hover:text-white border border-white/[0.06] hover:border-violet-500/30'
              }`}
            >
              <span>{CAT_ICON[cat]}</span>{cat}
            </motion.button>
          ))}
        </motion.div>

        {/* Skills grid */}
        <AnimatePresence mode="wait">
          <motion.div key={active}
            initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-10}}
            transition={{duration:0.3}}
            className="grid md:grid-cols-2 gap-8"
          >
            {/* Bars */}
            <div className="glass rounded-3xl p-8 border border-white/[0.06]">
              <h3 className="text-white font-bold text-lg mb-7 flex items-center gap-2">
                <span>{CAT_ICON[active]}</span> {active} Skills
              </h3>
              {SKILLS[active].map((s, i) => <Bar key={s.name} {...s} i={i} />)}
            </div>

            {/* Cards */}
            <div className="grid grid-cols-2 gap-3">
              {SKILLS[active].map((s, i) => (
                <motion.div key={s.name}
                  initial={{opacity:0,scale:0.85}} animate={{opacity:1,scale:1}}
                  transition={{delay:i*0.07}}
                  whileHover={{scale:1.05,y:-4}}
                  className="glass border border-white/[0.06] hover:border-violet-500/25 rounded-2xl p-5 text-center card-lift transition-colors"
                >
                  <div className="text-3xl mb-3">{s.icon}</div>
                  <p className="text-white text-sm font-semibold mb-2">{s.name}</p>
                  <div className="h-1 bg-white/[0.05] rounded-full overflow-hidden mx-auto max-w-[60px]">
                    <motion.div className="h-full progress rounded-full"
                      initial={{width:0}} animate={{width:`${s.pct}%`}}
                      transition={{duration:1,delay:i*0.07+0.2}} />
                  </div>
                  <p className="text-violet-400 text-xs font-mono mt-1">{s.pct}%</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Tech pill row */}
        <motion.div initial={{opacity:0,y:16}} whileInView={{opacity:1,y:0}}
          transition={{duration:0.5,delay:0.2}} viewport={{once:true}}
          className="mt-16 pt-10 border-t border-white/[0.05]"
        >
          <p className="text-center text-slate-600 text-[11px] font-mono tracking-[0.2em] uppercase mb-7">Core Technologies</p>
          <div className="flex flex-wrap justify-center gap-3">
            {['React.js','Laravel','TypeScript','MySQL','Tailwind CSS','PHP','Git','REST APIs','Redis','Docker'].map((t,i) => (
              <motion.span key={t}
                initial={{opacity:0,y:8}} whileInView={{opacity:1,y:0}}
                transition={{delay:i*0.04}} viewport={{once:true}}
                whileHover={{scale:1.1,y:-2}}
                className="px-4 py-2 glass border border-white/[0.06] hover:border-violet-500/30 rounded-full text-slate-400 hover:text-violet-300 text-xs font-mono cursor-default transition-all"
              >{t}</motion.span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
